import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Info, Printer } from "lucide-react";
import DownloadButton from "./DownloadButton";
import { fetchFilteredTransactionByCode, fetchTransactionByCodeAndId } from "../apis/transaction";
import { getFieldMaster } from "../apis/fieldMaster";
import { displayToast } from "../utils/Toast";

const WorkOrderSalesOrderDownload = ({ listDownloadProps }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [salesOrderNo, setSalesOrderNo] = useState("");
	const [isPreparing, setIsPreparing] = useState(false);

	const handlePrint = async () => {
		const reference = salesOrderNo.trim().toUpperCase();
		if (!reference || isPreparing) return;
		setIsPreparing(true);
		try {
			const salesOrderField = (listDownloadProps.tableData?.headings || []).find((field) =>
				["sales_order_no", "sales_order_number"].includes(field.field_name) ||
				/sales order (no|number)/i.test(field.display_name || ""),
			)?.field_name || "sales_order_no";
			const workOrders = [];
			const seenIds = new Set();
			let page = 1;
			while (true) {
				const response = await fetchFilteredTransactionByCode({
					transactionCode: "WO", page, pageSize: 20,
					filterData: { filter_data: [{ field_name: salesOrderField, operation: "=", value: reference }] },
					sortBy: "tran_id", sortDir: "asc",
				});
				if (response?.description) throw new Error(response.description);
				if (!Array.isArray(response?.TransactionList)) throw new Error("Unable to load matching work orders.");
				for (const row of response.TransactionList) {
					if (row.tran_id == null || seenIds.has(String(row.tran_id))) {
						throw new Error("Unable to retrieve all matching work orders.");
					}
					seenIds.add(String(row.tran_id));
					workOrders.push(row);
				}
				if (response.TransactionList.length < 20) break;
				page += 1;
			}
			if (!workOrders.length) throw new Error(`No work orders found for ${reference}.`);

			const fieldMaster = await getFieldMaster({ transactionCode: "WO" });
			if (fieldMaster?.description) throw new Error(fieldMaster.description);
			if (!Array.isArray(fieldMaster?.items?.item_fields)) throw new Error("Unable to load Work Order print fields.");
			const details = [];
			for (const workOrder of workOrders) {
				const detail = await fetchTransactionByCodeAndId("WO", workOrder.tran_id);
				if (detail?.description) throw new Error(detail.description);
				if (!detail?.head_data || !Array.isArray(detail.item_data)) throw new Error("Unable to load complete Work Order details.");
				const linkedReference = detail.head_data[salesOrderField] || detail.head_data.sales_order_no || detail.head_data.sales_order_number;
				if (String(linkedReference || "").trim().toUpperCase() !== reference) {
					throw new Error("A returned work order does not match this sales order.");
				}
				details.push(detail);
			}

			const titles = ["Product Name", "Quantity", "UOM", "Delivery Date", "Produced Quantity", "Accepted Quantity", "Rejected Quantity"];
			const rankField = (field) => {
				const name = `${field.title || ""} ${field.field_name || ""}`.toLowerCase();
				if (/product[\s_-]+name/.test(name)) return 0;
				if (/delivery[\s_-]+date|expected[\s_-]+delivery/.test(name)) return 3;
				if (/qty|quantity/.test(name)) {
					if (/produced/.test(name)) return 4;
					if (/accepted|approved/.test(name)) return 5;
					if (/rejected/.test(name)) return 6;
					return 1;
				}
				if (/\buom\b|\bunit\b|unit_of_measure/.test(name)) return 2;
				return titles.length;
			};
			const fields = fieldMaster.items.item_fields.filter((field) =>
				!field.is_internal_field && !/engineering[\s_-]+(?:dwg|drawing)/i.test(`${field.title || ""} ${field.field_name || ""}`),
			);
			if (!fields.some((field) => rankField(field) === 3)) fields.push({ field_name: "delivery_date", title: "Delivery Date" });
			const rowData = fields
				.map((field) => ({ ...field, title: titles[rankField(field)] || field.title }))
				.sort((firstField, secondField) => rankField(firstField) - rankField(secondField))
				.reverse();
			const first = details[0];
			const sharedValue = (field) => details.every((detail) => detail.head_data[field] === first.head_data[field]) ? first.head_data[field] : "";
			const itemDetails = details.flatMap((detail) => detail.item_data.map((item) => ({
				...item,
				source_work_order_no: detail.head_data.tran_no,
				delivery_date: item.delivery_date || item.expected_delivery || item.expected_delivery_date || detail.head_data.expected_delivery,
			})));
			if (!itemDetails.length) throw new Error("No Work Order items found for this sales order.");
			navigate(`${location.pathname.replace(/\/$/, "")}/transactions/invoice`, {
				state: {
					selectedReportName: "Work Order",
					formData: {
						transactionCode: "WO", rowData, itemDetails,
						companyData: first.company_data || {},
						headDetails: {
							...first.head_data,
							tran_no: details.length === 1 ? first.head_data.tran_no : "Multiple",
							sales_order_no: reference,
							date: sharedValue("date"), created_at: sharedValue("created_at"),
							type: sharedValue("type"), dc_no: sharedValue("dc_no"), dc_date: sharedValue("dc_date"),
							expected_delivery: sharedValue("expected_delivery"),
							prepared_by: sharedValue("prepared_by"), revised_by: sharedValue("revised_by"),
							approved_by: sharedValue("approved_by"), rev_no: sharedValue("rev_no"), rev_date: sharedValue("rev_date"),
						},
					},
				},
			});
		} catch (error) {
			displayToast(error?.message || "Unable to open Work Order print preview.", "error");
		} finally {
			setIsPreparing(false);
		}
	};

	const [showPrintHelp, setShowPrintHelp] = useState(false);

	return (
		<>
			<div className="work-order-print-input-group">
				<input
					className="work-order-sales-order-input"
					type="text"
					aria-label="Sales order number for Work Order print preview"
					placeholder="Sales Order No"
					value={salesOrderNo}
					disabled={isPreparing}
					onChange={(event) => setSalesOrderNo(event.target.value)}
				/>
				<button
					type="button"
					className="work-order-print-help-trigger"
					aria-label="Sales order print help"
					aria-describedby="work-order-print-tip"
					aria-expanded={showPrintHelp}
					onClick={() => setShowPrintHelp((visible) => !visible)}
					onBlur={() => setShowPrintHelp(false)}
				>
					<Info size={16} />
				</button>
				<span id="work-order-print-tip" role="tooltip" className={`work-order-print-tip${showPrintHelp ? " is-open" : ""}`}>
					Enter a sales order number (e.g. SO/26-27/00049) and select Print to preview its work orders, then download the PDF from the preview.
				</span>
			</div>
			{salesOrderNo.trim() && (
				<button type="button" className="list-reset-button" onClick={handlePrint} disabled={isPreparing} title="Print work orders for this sales order">
					<Printer size={16} />
					{isPreparing ? "Loading..." : "Print"}
				</button>
			)}
			<DownloadButton {...listDownloadProps} />
		</>
	);
};

export default WorkOrderSalesOrderDownload;
